import { useEffect } from 'react'
import { DSSubmitProductVoteResult, DSVote } from 'shared-definitions/types'
import { create } from 'zustand'

interface Opts {
  productId: string
  backendVotes: DSSubmitProductVoteResult
}

interface Result {
  votesLoaded: boolean
  voteInfo: VoteInfo
  setVoteInfo: (voteInfo: VoteInfo) => void
}

interface VoteInfo {
  userVote: DSVote | null
  votesCount: DSSubmitProductVoteResult
}

interface ProductVoteState {
  votesLoaded: boolean
  voteInfo: VoteInfo
}

interface VotesStore {
  products: Record<string, ProductVoteState>
  initializeProduct: (productId: string, backendVotes: DSSubmitProductVoteResult) => void
  updateProductVotes: (productId: string, voteInfo: VoteInfo) => void
}

const USER_VOTE_PREFIX = 'product-v4-user-vote-'
const VOTES_COUNT_PREFIX = 'product-v4-votes-count-'

// Zustand store for managing product votes
const useVotesStore = create<VotesStore>()((set, get) => ({
  products: {},

  initializeProduct: (productId: string, backendVotes: DSSubmitProductVoteResult) => {
    const existing = get().products[productId]
    if (existing?.votesLoaded) {
      return
    }

    const saved = getSavedSession(productId)
    const voteInfo: VoteInfo = saved
      ? {
          userVote: saved.userVote,
          votesCount: saved.votesCount || backendVotes,
        }
      : {
          userVote: null,
          votesCount: backendVotes,
        }

    set(state => ({
      products: {
        ...state.products,
        [productId]: {
          votesLoaded: true,
          voteInfo,
        },
      },
    }))
  },

  updateProductVotes: (productId: string, voteInfo: VoteInfo) => {
    set(state => ({
      products: {
        ...state.products,
        [productId]: {
          ...state.products[productId],
          voteInfo,
        },
      },
    }))

    // Persist to storage
    saveSession(productId, voteInfo)
  },
}))

export function useProductVotes(opts: Opts): Result {
  const store = useVotesStore()
  const productState = useVotesStore(state => state.products[opts.productId])

  useEffect(() => {
    store.initializeProduct(opts.productId, opts.backendVotes)
  }, [store, opts.productId, opts.backendVotes])

  const defaultState: ProductVoteState = {
    votesLoaded: false,
    voteInfo: {
      userVote: null,
      votesCount: opts.backendVotes,
    },
  }

  const currentState = productState || defaultState

  return {
    votesLoaded: currentState.votesLoaded,
    voteInfo: currentState.voteInfo,
    setVoteInfo: (newVoteInfo: VoteInfo) => {
      store.updateProductVotes(opts.productId, newVoteInfo)
    },
  }
}

function saveSession(productId: string, voteInfo: VoteInfo): void {
  localStorage.setItem(`${USER_VOTE_PREFIX}${productId}`, JSON.stringify(voteInfo.userVote))
  sessionStorage.setItem(`${VOTES_COUNT_PREFIX}${productId}`, JSON.stringify(voteInfo.votesCount))
}

function getSavedSession(
  productId: string
): { votesCount: DSSubmitProductVoteResult | null; userVote: DSVote } | null {
  const userVoteJson = localStorage.getItem(`${USER_VOTE_PREFIX}${productId}`)
  if (userVoteJson) {
    const votesCountJson = sessionStorage.getItem(`${VOTES_COUNT_PREFIX}${productId}`)
    const votesCount = votesCountJson
      ? (JSON.parse(votesCountJson) as DSSubmitProductVoteResult)
      : null

    return {
      votesCount,
      userVote: JSON.parse(userVoteJson) as DSVote,
    }
  }

  return null
}
